import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { IconDownload } from '@tabler/icons-react';
import axios from '../../../../library/axios';
import {
  downloadCentralUserImportTemplate,
  downloadHospitalUserImportTemplate,
} from '../../../../helpers/cpanelImportTemplates';
import {
  handleJobQueuedSuccess,
  handleJobRequestError,
} from '../../../../helpers/cpanelJobFeedback';
import { USER_SCOPE_CENTRAL } from './constants';
import {
  hasValidationErrors,
  validateImportFile,
  IMPORT_ALLOWED_EXTENSIONS,
  IMPORT_MAX_FILE_BYTES,
} from './validation';

const ImportCpanelUserComp = ({ scope, tenantSlug, tenants = [], close, fetch }) => {
  const isCentral = scope === USER_SCOPE_CENTRAL;
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [importTenantSlug, setImportTenantSlug] = useState(tenantSlug || '');

  useEffect(() => {
    setImportTenantSlug(tenantSlug || '');
    setFile(null);
    setErrors({});
  }, [scope, tenantSlug]);

  const handleImport = async (event) => {
    event?.preventDefault();
    const nextErrors = { ...validateImportFile(file) };
    if (!isCentral && !importTenantSlug) {
      nextErrors.tenantSlug = 'Pilih tenant untuk impor pengguna rumah sakit.';
    }
    setErrors(nextErrors);
    if (hasValidationErrors(nextErrors)) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('filePath', file);
    formData.append('scope', scope);
    if (!isCentral) formData.append('tenantSlug', importTenantSlug);

    try {
      const response = await axios.post('/cpanel/users/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await handleJobQueuedSuccess({
        response,
        actionLabel: isCentral ? 'Import pengguna CPanel Center' : 'Import pengguna rumah sakit',
        close,
        fetch,
      });
    } catch (err) {
      handleJobRequestError(err, 'Tidak dapat memulai import pengguna.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    if (isCentral) {
      downloadCentralUserImportTemplate();
      return;
    }
    downloadHospitalUserImportTemplate();
  };

  return (
    <form noValidate onSubmit={handleImport}>
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <p className="text-muted small mb-0">
          {isCentral
            ? 'Gunakan template XLSX. Kolom wajib: username, password, full_name, role (superadmin atau operator). Kolom email dan active opsional. Unggah file CSV, XLS, atau XLSX.'
            : 'Gunakan template XLSX. Tenant dipilih di form. Kolom wajib: username, password, nama_lengkap, email, role (nama group di RS). Kolom nik dan jabatan opsional. Unggah file CSV, XLS, atau XLSX.'}
        </p>
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          className="flex-shrink-0"
          onClick={handleDownloadTemplate}
        >
          <IconDownload size={16} stroke={1.75} className="me-1" />
          Template XLSX
        </Button>
      </div>
      {!isCentral ? (
        <div className="mb-4">
          <label className="form-label required">Tenant tujuan</label>
          <select
            className={`form-select${errors.tenantSlug ? ' is-invalid' : ''}`}
            value={importTenantSlug}
            onChange={(e) => {
              setImportTenantSlug(e.target.value);
              if (errors.tenantSlug) {
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.tenantSlug;
                  return next;
                });
              }
            }}
          >
            <option value="">Pilih tenant</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.slug}>
                {t.slug} - {t.description}
              </option>
            ))}
          </select>
          {errors.tenantSlug ? <div className="invalid-feedback d-block">{errors.tenantSlug}</div> : null}
        </div>
      ) : null}
      <div className="mb-4">
        <label className="form-label required">File</label>
        <input
          type="file"
          className={`form-control${errors.file ? ' is-invalid' : ''}`}
          accept={IMPORT_ALLOWED_EXTENSIONS.join(',')}
          onChange={(e) => {
            const selected = e.target.files?.[0] || null;
            setFile(selected);
            setErrors(validateImportFile(selected));
          }}
        />
        {errors.file ? <div className="invalid-feedback d-block">{errors.file}</div> : null}
        <div className="form-text">Maks. {IMPORT_MAX_FILE_BYTES / (1024 * 1024)} MB — CSV, XLS, atau XLSX</div>
      </div>
      <div className="d-flex gap-2 justify-content-end">
        <Button variant="light" onClick={close} disabled={loading}>
          Batal
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? 'Memproses...' : 'Impor'}
        </Button>
      </div>
    </form>
  );
};

export default ImportCpanelUserComp;
